import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, User, Lock, ArrowRight, Wallet } from 'lucide-react';

export const PortalLoginView: React.FC = () => {
  const navigate = useNavigate();
  const [nis, setNis] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // If already logged in, redirect to dashboard
  useEffect(() => {
    const existingId = localStorage.getItem('portal_siswa_id');
    if (existingId) {
      navigate('/portal/dashboard', { replace: true });
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nis || !password) {
      setError('NIS dan Password harus diisi');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const res = await fetch('/api/auth/portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nis, password }),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!res.ok && !contentType.includes('application/json')) {
        setError(`Server backend tidak dapat dihubungi (${res.status} ${res.statusText || 'Service Unavailable'}). Pastikan layanan backend aktif.`);
        return;
      }

      const data = await res.json().catch(() => ({}));
      
      if (data.success) {
        // Simpan token/sesi secara lokal untuk demo (di production gunakan HttpOnly Cookie / JWT)
        localStorage.setItem('portal_token', data.token);
        localStorage.setItem('portal_siswa_id', data.siswa.id);
        navigate('../dashboard', { replace: true });
      } else {
        setError(data.message || 'Login gagal, periksa NIS dan Password.');
      }
    } catch (err: any) {
      setError(err?.message || 'Terjadi kesalahan jaringan.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full w-full bg-slate-900 flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-[-10%] right-[-20%] w-64 h-64 bg-sky-500 rounded-full blur-[100px] opacity-20"></div>
      <div className="absolute bottom-[-10%] left-[-20%] w-64 h-64 bg-indigo-500 rounded-full blur-[100px] opacity-20"></div>

      <div className="w-full max-w-sm z-10">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-sky-500/10 border border-sky-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-xl">
            <Wallet className="w-10 h-10 text-sky-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">SDMT App</h1>
          <p className="text-slate-400 text-sm">Portal Tagihan & Keuangan Siswa</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-3 rounded-xl text-center backdrop-blur-md">
              {error}
            </div>
          )}

          <div>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-sky-400 transition-colors">
                <User className="w-5 h-5" />
              </div>
              <input
                id="portal-nis"
                type="text"
                autoComplete="username"
                placeholder="Nomor Induk Siswa (NIS)"
                className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-2xl pl-12 pr-4 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500/50 transition-all backdrop-blur-xl"
                value={nis}
                onChange={(e) => setNis(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-sky-400 transition-colors">
                <Lock className="w-5 h-5" />
              </div>
              <input
                id="portal-password"
                type="password"
                autoComplete="current-password"
                placeholder="Password (Default: 123456)"
                className="w-full bg-slate-800/50 border border-slate-700/50 text-white rounded-2xl pl-12 pr-4 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500/50 transition-all backdrop-blur-xl"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-sky-500 hover:bg-sky-400 text-white rounded-2xl py-4 font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-sky-500/20 mt-2"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Masuk ke Portal
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4" />
          <span>Aman & Terenkripsi</span>
        </div>
      </div>
    </div>
  );
};
